// Types en klok voor de spelstaat. De server stuurt bij elke wijziging de
// volledige staat, per ontvanger opgebouwd. Dat is iets meer verkeer dan
// losse deltas, maar het scheelt een hele klasse desync-bugs.

export type Phase = 'lobby' | 'preview' | 'playing' | 'turn_over' | 'game_over'

export interface GamePlayer {
  user_id: string
  name: string
  avatar_hash: string | null
  team_id: string | null
}

export interface GameTeam {
  id: string
  name: string
  score: number
  member_ids: string[]
}

export interface WordCard {
  /** Gevuld zodra je het woord mag zien, anders null. */
  word: string | null
  /** Lengte per los woord in de term, voor de streepjes. */
  pattern: number[]
  result: 'correct' | 'pass' | null
  current: boolean
}

export interface GameTurn {
  team_id: string
  describer_id: string
  describer_name: string
  correct: number
  passed: number
  words_total: number
  words_done: number
  ends_at: number
  you_describe: boolean
  /** Alleen bij touwtrekken: wat er deze beurt getrokken is. */
  pulled: number | null
  rope_after: number | null
  /** Alleen gevuld bij de omschrijver. De rest krijgt hier null. */
  current_word: string | null
  /** De kaart zoals deze kijker hem mag zien. */
  cards: WordCard[]
  /** Zit je in het team dat aan de beurt is? Dan mag je meeroepen. */
  you_guess: boolean
  /** Loopt de klok al, of staat de kaart nog in de preview? */
  started: boolean
}

export type Scoring = 'points' | 'tug'

export interface GameState {
  phase: Phase
  teams: GameTeam[]
  players: GamePlayer[]
  turn: GameTurn | null
  winner_team_id: string | null
  host_id: string | null
  you_are_host: boolean
  target_score: number
  turn_seconds: number
  preview_seconds: number
  last_end_reason: string | null
  language: string
  scoring: Scoring
  /** Staat meeluisteren aan (host-instelling)? */
  listening: boolean
  /** Servertijd waarop meeluisteren weer aan mag. 0 als het al aan staat. */
  listening_ready_at: number
  /** Gaan de microfoons op dit moment echt open? */
  listening_now: boolean
  /** Is er uberhaupt een bot verbonden? */
  bot_online: boolean
  rope: number
  rope_size: number
  turns_played: number
  max_turns: number
  is_draw: boolean
  word_count: number
  server_time: number
}

/**
 * Rekent de servertijd om naar lokale tijd.
 *
 * De browser telt alleen visueel af, de server bepaalt wanneer de beurt echt
 * stopt. Dit voorkomt dat een verzette systeemklok of een trage machine een
 * ander aantal seconden op het scherm zet.
 *
 * De afwijking bevat de halve netwerklatency. Voor een klok van 30 seconden
 * is dat ruim voldoende nauwkeurig.
 */
export class ServerClock {
  private offset = 0

  sync(serverTime: number): void {
    this.offset = serverTime - Date.now() / 1000
  }

  now(): number {
    return Date.now() / 1000 + this.offset
  }

  remaining(endsAt: number): number {
    return Math.max(0, endsAt - this.now())
  }
}

export function teamOf(state: GameState, userId: string): GameTeam | null {
  const player = state.players.find((p) => p.user_id === userId)
  if (!player?.team_id) return null
  return state.teams.find((t) => t.id === player.team_id) ?? null
}

export function playersOfTeam(state: GameState, teamId: string): GamePlayer[] {
  return state.players.filter((p) => p.team_id === teamId)
}

